import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Printer, Clock, Euro, CheckCircle, ChevronDown, ChevronUp, ShoppingCart, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { formatPrice } from '../../lib/utils';
import { printSingleProduct, printPromotionalPoster } from '../../utils/printTemplates';
import { toast } from 'sonner';

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

export const MyOffersTab = ({
    myOffers,
    user,
    editingOfferId,
    setEditingOfferId,
    offerCustomPrices,
    onOfferPriceChange,
    getOfferProductPrice,
    companyName,
    setCompanyName,
    posterModel,
    onOpenOrderDialog,
}) => {
    const navigate = useNavigate();

    if (myOffers.length === 0) {
        return (
            <Card>
                <CardContent className="py-12 text-center">
                    <Gift className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground text-lg mb-2">Aucune offre personnalisée</p>
                    <p className="text-sm text-muted-foreground">
                        Les offres spéciales créées pour vous par PRIXDEAL apparaîtront ici
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {myOffers.map((offer) => {
                const validation = (offer.validations || []).find(v => v.client_id === user?.id);
                return (
                    <Card key={offer.id} className={`border-2 overflow-hidden ${validation ? 'border-green-300 bg-gradient-to-br from-green-50 to-white' : 'border-amber-200 bg-gradient-to-br from-amber-50 to-white'}`} data-testid={`my-offer-${offer.id}`}>
                        <CardHeader className={`${validation ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-amber-500 to-orange-500'} text-white`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    {validation ? (
                                        <Badge className="bg-white/20 text-white mb-2" data-testid={`offer-validated-${offer.id}`}>
                                            <CheckCircle className="h-3 w-3 mr-1" />Offre validee
                                        </Badge>
                                    ) : (
                                        <Badge className="bg-white/20 text-white mb-2">
                                            <Gift className="h-3 w-3 mr-1" />Offre exclusive
                                        </Badge>
                                    )}
                                    <CardTitle className="text-2xl">{offer.title}</CardTitle>
                                    {offer.description && <CardDescription className="text-white/90 mt-2">{offer.description}</CardDescription>}
                                </div>
                                <div className="text-right">
                                    {offer.expiry_date && (
                                        <div className="flex items-center gap-1 text-sm bg-white/20 px-3 py-1 rounded-full">
                                            <Clock className="h-4 w-4" />Expire le {formatDate(offer.expiry_date)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <p className="text-sm text-muted-foreground">
                                    {offer.products?.length || 0} produit(s) en promotion exclusive pour vous.
                                </p>
                                <Button
                                    variant={editingOfferId === offer.id ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setEditingOfferId(editingOfferId === offer.id ? null : offer.id)}
                                    data-testid={`toggle-prices-${offer.id}`}
                                >
                                    <Euro className="h-4 w-4 mr-1" />
                                    {editingOfferId === offer.id ? 'Masquer les prix' : 'Saisir mes prix de vente'}
                                    {editingOfferId === offer.id ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                                </Button>
                            </div>

                            {editingOfferId === offer.id && (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6" data-testid={`price-editor-${offer.id}`}>
                                    <h4 className="font-semibold text-green-800 mb-1 flex items-center gap-2">
                                        <Euro className="h-4 w-4" />Saisissez vos prix de vente
                                    </h4>
                                    <p className="text-xs text-green-600 mb-3">Entrez vos prix de vente pour chaque produit. Ces prix seront affichés sur l'affiche imprimée.</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {offer.products?.map((product) => (
                                            <div key={product.product_id} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-green-100">
                                                {product.image_url && <img src={product.image_url} alt={product.product_name || product.name} className="w-10 h-10 object-cover rounded" />}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{product.product_name || product.name}</div>
                                                    <div className="text-xs text-muted-foreground">Prix fournisseur: {formatPrice(product.price || product.offer_price || 0)}</div>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <Input type="number" step="0.01" min="0"
                                                        value={offerCustomPrices[offer.id]?.[product.product_id] ?? ''}
                                                        onChange={(e) => onOfferPriceChange(offer.id, product.product_id, e.target.value)}
                                                        placeholder={(product.price || product.offer_price || 0).toFixed(2)}
                                                        className="w-24 h-8 text-right text-sm"
                                                        data-testid={`offer-price-input-${product.product_id}`}
                                                    />
                                                    <span className="text-xs text-muted-foreground whitespace-nowrap">€/{product.unit}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Products grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {offer.products?.map((product) => {
                                    const displayPrice = getOfferProductPrice(offer.id, product);
                                    return (
                                        <div key={product.product_id}
                                            className="border rounded-xl p-3 text-center relative group hover:shadow-lg hover:border-amber-400 transition-all cursor-pointer bg-white"
                                            onClick={() => {
                                                printSingleProduct({
                                                    id: product.product_id, name: product.product_name || product.name,
                                                    image_url: product.image_url, origin: product.origin, unit: product.unit,
                                                    price: displayPrice, customPrice: displayPrice,
                                                    season_start: product.season_start, season_end: product.season_end,
                                                }, companyName, posterModel);
                                                toast.success(`Impression de ${product.product_name || product.name}`);
                                            }}>
                                            {product.original_price > 0 && displayPrice < product.original_price && (
                                                <Badge className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs z-10">
                                                    -{Math.round((1 - displayPrice / product.original_price) * 100)}%
                                                </Badge>
                                            )}
                                            {(!product.original_price || displayPrice >= product.original_price) && (
                                                <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs z-10">PROMO</Badge>
                                            )}
                                            <div className="absolute inset-0 bg-amber-500/90 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center z-20">
                                                <div className="text-white text-center">
                                                    <Printer className="h-8 w-8 mx-auto mb-2" /><span className="text-sm font-medium">Imprimer</span>
                                                </div>
                                            </div>
                                            {product.image_url && <img src={product.image_url} alt={product.product_name || product.name} className="w-full h-24 object-cover rounded-lg mb-2" />}
                                            <div className="font-medium text-sm truncate">{product.product_name || product.name}</div>
                                            {product.origin && <div className="text-xs text-muted-foreground">{product.origin}</div>}
                                            <div className="mt-2 space-y-1">
                                                {offerCustomPrices[offer.id]?.[product.product_id] !== undefined && offerCustomPrices[offer.id]?.[product.product_id] !== '' && (
                                                    <div className="text-xs text-muted-foreground">Fournisseur: {formatPrice(product.price || product.offer_price || 0)}</div>
                                                )}
                                                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg p-2">
                                                    <div className="text-xs opacity-75">
                                                        {offerCustomPrices[offer.id]?.[product.product_id] !== undefined && offerCustomPrices[offer.id]?.[product.product_id] !== '' ? 'Mon prix de vente' : 'Prix fournisseur'}
                                                    </div>
                                                    <div className="text-lg font-bold">{formatPrice(displayPrice)}</div>
                                                    <div className="text-xs opacity-90">/ {product.unit}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Actions */}
                            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                                <div className="flex-1 max-w-xs">
                                    <Label htmlFor={`company-${offer.id}`} className="text-xs text-muted-foreground">Nom de votre enseigne</Label>
                                    <Input id={`company-${offer.id}`} value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Ex: Franprix Paris 15" className="mt-1 h-9" />
                                </div>
                                <Button onClick={() => {
                                    const productsWithPrices = offer.products.map(p => ({
                                        id: p.product_id, name: p.product_name || p.name, image_url: p.image_url,
                                        origin: p.origin, unit: p.unit,
                                        price: getOfferProductPrice(offer.id, p), customPrice: getOfferProductPrice(offer.id, p),
                                    }));
                                    printPromotionalPoster(productsWithPrices, companyName);
                                    toast.success('Ouverture de l\'affiche complète');
                                }} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" size="lg" data-testid={`print-all-offer-${offer.id}`}>
                                    <Printer className="h-5 w-5 mr-2" />Imprimer l'affiche avec mes prix
                                </Button>
                                <Button onClick={() => onOpenOrderDialog(offer)} className="bg-jungle hover:bg-jungle/90 text-white" size="lg"
                                    data-testid={`order-offer-${offer.id}`} disabled={!!validation}>
                                    <ShoppingCart className="h-5 w-5 mr-2" />{validation ? 'Offre deja commandee' : 'Commander cette offre'}
                                </Button>
                                {validation && (
                                    <Button onClick={() => navigate(`/dashboard/commande/${validation.order_id}`)} className="bg-green-600 hover:bg-green-700 text-white" size="lg" data-testid={`view-order-${offer.id}`}>
                                        <FileText className="h-5 w-5 mr-2" />Voir commande n°{validation.order_number}
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};
